<template>
  <div class="page-venues">
    <section class="mx-auto w-full max-w-[1480px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      <div class="overflow-hidden rounded-[34px] border border-[#e9d8c7] bg-[radial-gradient(circle_at_top_left,#fffaf2_0%,#f8efe5_45%,#f2e7dc_100%)] shadow-[0_24px_70px_rgba(88,60,37,0.08)]">
        <div class="grid gap-8 px-5 py-6 sm:px-7 sm:py-8 xl:grid-cols-[minmax(0,1.1fr)_360px] xl:px-8">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.24em] text-[#b78969]">Public venues</p>
            <h1 class="mt-3 max-w-3xl font-serif text-[2.35rem] font-semibold leading-[1.02] text-ink sm:text-[2.9rem] lg:text-[3.5rem]">
              Find spaces that fit the event, not just the search.
            </h1>
            <p class="mt-4 max-w-2xl text-[1.02rem] leading-8 text-[#6f5948] sm:text-[1.08rem]">
              Browse venue cards, switch event chips instantly, and use the live map to compare where each shortlist sits before opening the full invite detail.
            </p>

            <div class="mt-6 flex flex-wrap gap-3">
              <span
                v-for="stat in venueStats"
                :key="stat.label"
                class="rounded-[18px] border border-[#eadfd4] bg-white/85 px-4 py-3 shadow-sm"
              >
                <span class="block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b78969]">{{ stat.label }}</span>
                <span class="mt-1 block text-sm font-semibold text-ink">{{ stat.value }}</span>
              </span>
            </div>
          </div>

          <div class="rounded-[28px] border border-[#ead8cb] bg-white/90 p-5 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Search summary</p>
            <div class="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <div class="rounded-[18px] bg-[#fbf6f1] px-4 py-4">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#b78969]">Location</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ locationLabel || 'All locations' }}</p>
              </div>
              <div class="rounded-[18px] bg-[#fbf6f1] px-4 py-4">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#b78969]">Search term</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ searchKey || 'No keyword filter' }}</p>
              </div>
              <div class="rounded-[18px] bg-[#fbf6f1] px-4 py-4">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#b78969]">Event filter</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ eventType || 'All venue events' }}</p>
              </div>
              <div class="rounded-[18px] bg-[#fbf6f1] px-4 py-4">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#b78969]">Matches</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ filteredVenues.length }} venues</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_430px]">
        <div class="space-y-5">
          <div class="rounded-[28px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue events hosted</p>
                <h2 class="mt-2 text-lg font-semibold text-ink">Filter by event type</h2>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <button
                  v-for="chip in eventTypeChips"
                  :key="chip.value || 'all'"
                  type="button"
                  class="rounded-full border px-4 py-2 text-sm font-semibold transition"
                  :class="activeEventTypeChip === chip.value ? 'border-brand bg-[#fbf1e6] text-brand shadow-sm' : 'border-[#eadfd4] bg-white text-[#6f5948] hover:border-brand hover:text-brand'"
                  @click="selectEventTypeChip(chip.value)"
                >
                  {{ chip.label }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="loading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm font-semibold text-muted">
            Loading venues...
          </div>
          <div v-else-if="error" class="rounded-[24px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {{ error }}
          </div>
          <div v-else-if="filteredVenues.length" class="grid gap-4 lg:grid-cols-2">
            <article
              v-for="venue in filteredVenues"
              :key="venue.id"
              class="group overflow-hidden rounded-[28px] border border-[#eadfd4] bg-white shadow-[0_10px_30px_rgba(102,73,44,0.05)] transition hover:-translate-y-1 hover:border-[#d9b594] hover:shadow-[0_18px_40px_rgba(102,73,44,0.12)]"
            >
              <div class="relative h-52 overflow-hidden bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] sm:h-56">
                <img v-if="venue.image" :src="venue.image" :alt="venue.title" class="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]">
                <div v-else class="flex h-full items-center justify-center text-[2.4rem] font-semibold text-[#c69225]">
                  {{ venue.title.slice(0, 1) }}
                </div>
                <div class="absolute inset-x-0 top-0 flex items-start justify-between gap-3 px-4 py-4">
                  <span class="rounded-full bg-[#1f1208]/65 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
                    {{ venue.typeLabel }}
                  </span>
                  <span v-if="venue.distanceLabel" class="rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-[#6b4d39] shadow-sm">
                    {{ venue.distanceLabel }}
                  </span>
                </div>
              </div>

              <div class="p-5 sm:p-6">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h2 class="text-[1.35rem] font-semibold text-ink sm:text-[1.45rem]">{{ venue.title }}</h2>
                    <p class="mt-2 text-sm leading-7 text-[#7b624d]">{{ venue.tagLine || 'Venue details available on inquiry.' }}</p>
                  </div>
                </div>

                <div class="mt-4 flex flex-wrap gap-2">
                  <span
                    v-for="item in venue.highlights.slice(0, 3)"
                    :key="item"
                    class="rounded-full bg-[#fbf1e6] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand"
                  >
                    {{ item }}
                  </span>
                </div>

                <div class="mt-5 space-y-3 text-sm text-muted">
                  <p class="inline-flex items-start gap-2">
                    <i class="ri-map-pin-line mt-0.5 text-brand"></i>
                    <span>{{ venue.address }}</span>
                  </p>
                  <p class="inline-flex items-center gap-2">
                    <i class="ri-calendar-2-line text-brand"></i>
                    <span>{{ venue.estLabel }}</span>
                  </p>
                </div>

                <div class="mt-6 flex items-center justify-between gap-3 border-t border-[#f1e5da] pt-4">
                  <span class="text-xs font-semibold uppercase tracking-[0.16em] text-[#b78969]">
                    {{ venue.highlights.length ? `${venue.highlights.length} highlights` : 'Venue profile' }}
                  </span>
                  <RouterLink
                    :to="{ name: 'venue-invite-detail', params: { venueId: venue.id }, query: detailQuery }"
                    class="inline-flex items-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition hover:bg-brandDark"
                  >
                    View venue
                  </RouterLink>
                </div>
              </div>
            </article>
          </div>
          <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm text-muted">
            No venues found.
          </div>
        </div>

        <aside class="xl:sticky xl:top-6 self-start">
          <div class="overflow-hidden rounded-[30px] border border-[#eadfd4] bg-white shadow-[0_18px_44px_rgba(88,60,37,0.08)]">
            <div class="border-b border-[#f0e4d8] px-5 py-5">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Live map</p>
              <div class="mt-2 flex items-end justify-between gap-3">
                <div>
                  <h2 class="text-lg font-semibold text-ink">Explore the area</h2>
                  <p class="mt-1 text-sm text-[#7b624d]">Map pins update with your active venue chip and keyword filter.</p>
                </div>
                <span class="rounded-full bg-[#fbf1e6] px-3 py-1 text-xs font-semibold text-brand">
                  {{ filteredVenues.length }} pins
                </span>
              </div>
            </div>

            <div class="border-b border-[#f0e4d8] px-5 py-4">
              <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                <div class="rounded-[18px] bg-[#fbf7f2] px-4 py-3">
                  <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#b78969]">Active event chip</p>
                  <p class="mt-1 text-sm font-semibold text-ink">{{ eventType || 'All' }}</p>
                </div>
                <div class="rounded-[18px] bg-[#fbf7f2] px-4 py-3">
                  <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#b78969]">Map focus</p>
                  <p class="mt-1 text-sm font-semibold text-ink">{{ locationLabel || 'India' }}</p>
                </div>
              </div>
            </div>

            <div id="venues-map" class="h-[340px] w-full sm:h-[420px] xl:h-[720px]"></div>
          </div>
        </aside>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import api from '@/services/user/api'
import { useSearchStore } from '@/stores/search'

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const route = useRoute()
const router = useRouter()
const searchStore = useSearchStore()

const venues = ref([])
const venueEventHostedOptions = ref([])
const loading = ref(true)
const error = ref('')
const map = ref(null)
const markers = ref([])
const locationRequested = ref(false)

const requestId = computed(() => String(route.query.id || '').trim())
const latitude = computed(() => String(route.query.latitude || route.query.lat || searchStore.lat || '').trim())
const longitude = computed(() => String(route.query.longitude || route.query.lng || searchStore.lng || '').trim())
const eventType = computed(() => String(route.query.event_type || '').trim())
const activeEventTypeChip = computed(() => eventType.value)
const locationLabel = computed(() => String(route.query.location || searchStore.locationText || '').trim())
const searchKey = computed(() => String(route.query.search_key || '').trim())
const detailQuery = computed(() => ({
  location: route.query.location || undefined,
  search_key: route.query.search_key || undefined,
  id: route.query.id || undefined,
  event_type: route.query.event_type || undefined,
  latitude: latitude.value || undefined,
  longitude: longitude.value || undefined
}))

const venueStats = computed(() => ([
  {
    label: 'Visible venues',
    value: `${filteredVenues.value.length} listings`
  },
  {
    label: 'Search area',
    value: locationLabel.value || 'Open search'
  },
  {
    label: 'Event type',
    value: eventType.value || 'All events'
  }
]))

const eventTypeChips = computed(() => [
  { label: 'All', value: '' },
  ...venueEventHostedOptions.value.map((item) => ({
    label: item,
    value: item
  }))
])

const normalizeArrayPayload = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const normalizeOptionValue = (item) => {
  if (typeof item === 'string') return item.trim()
  return String(item?.title || item?.name || item?.label || item?.value || item?.type || '').trim()
}

const parseCoordinate = (...values) => {
  for (const value of values) {
    const normalized = Number(String(value ?? '').trim())
    if (Number.isFinite(normalized)) return normalized
  }
  return null
}

const normalizeVenue = (item = {}, index = 0) => ({
  id: String(item?.id || item?.venue_id || `venue-${index}`),
  title: String(item?.title || item?.name || 'Untitled venue').trim(),
  image: String(item?.cover_image || item?.image || '').trim(),
  tagLine: String(item?.tag_line || item?.tagLine || '').trim(),
  about: String(item?.about || '').trim(),
  address: String(item?.address || 'Location unavailable').trim(),
  latitude: parseCoordinate(item?.latitude, item?.lat, item?.venue_latitude, item?.location_latitude, item?.location_lat),
  longitude: parseCoordinate(item?.longitude, item?.lng, item?.lon, item?.venue_longitude, item?.location_longitude, item?.location_lng, item?.location_lon),
  highlights: String(item?.highlights || '').split(',').map((entry) => entry.trim()).filter(Boolean),
  typeLabel: String(item?.type_name || item?.type || 'Venue').trim() || 'Venue',
  estLabel: item?.est_year ? `Est. ${item.est_year}` : 'Year not specified',
  distanceLabel: item?.distance ? `${Number(item.distance).toFixed(1)} km away` : ''
})

const filteredVenues = computed(() => {
  const query = searchKey.value.toLowerCase()
  if (!query) return venues.value
  return venues.value.filter((venue) => {
    const haystack = [
      venue.title,
      venue.tagLine,
      venue.about,
      venue.address,
      venue.typeLabel,
      ...venue.highlights
    ].join(' ').toLowerCase()
    return haystack.includes(query)
  })
})

const loadVenueEventHostedOptions = async () => {
  try {
    const { data } = await api.getVenueEventsHosted(new FormData())
    const options = normalizeArrayPayload(data, ['events', 'venue_events_hosted', 'items', 'list'])
      .map(normalizeOptionValue)
      .filter(Boolean)
    venueEventHostedOptions.value = [...new Set(options)]
  } catch {
    venueEventHostedOptions.value = []
  }
}

const loadVenues = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('id', requestId.value || '')
    formData.append('latitude', latitude.value || '')
    formData.append('longitude', longitude.value || '')
    formData.append('event_type', eventType.value || '')
    const { data } = await api.getVenuesInvite(formData)
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data?.list) ? data.list : []
    venues.value = items.map(normalizeVenue)
  } catch (err) {
    error.value = err?.message || 'Failed to load venues.'
    venues.value = []
  } finally {
    loading.value = false
  }
}

const selectEventTypeChip = async (value) => {
  const nextValue = String(value || '').trim()
  if (nextValue === eventType.value) return

  await router.replace({
    query: {
      ...route.query,
      event_type: nextValue || undefined
    }
  })
}

const ensureMap = () => {
  if (map.value) return
  map.value = L.map('venues-map').setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(map.value)
}

const dropMarkers = () => {
  if (!map.value) return
  markers.value.forEach((marker) => marker.remove())
  markers.value = []

  const markerVenues = filteredVenues.value.filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
  markerVenues.forEach((venue) => {
    const marker = L.marker([venue.latitude, venue.longitude]).addTo(map.value)
    marker.bindPopup(`<strong>${venue.title}</strong><br>${venue.address}`)
    markers.value.push(marker)
  })

  map.value.invalidateSize()

  if (markers.value.length) {
    const group = L.featureGroup(markers.value)
    map.value.fitBounds(group.getBounds().pad(0.2))
  } else {
    map.value.setView([20.5937, 78.9629], 5)
  }
}

const ensureLocation = async () => {
  if (locationRequested.value) return
  if (latitude.value && longitude.value) return
  locationRequested.value = true
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        searchStore.setLocation({
          text: locationLabel.value || 'Current location',
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        resolve()
      },
      () => resolve(),
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })
}

onMounted(async () => {
  ensureMap()
  requestAnimationFrame(() => {
    map.value?.invalidateSize()
  })
  await ensureLocation()
  await loadVenueEventHostedOptions()
  await loadVenues()
  dropMarkers()
})

watch(
  () => [route.fullPath, searchStore.lat, searchStore.lng],
  async () => {
    await loadVenues()
    dropMarkers()
  }
)

watch(filteredVenues, () => {
  dropMarkers()
})
</script>

<style scoped>
.page-venues {
  min-height: 100vh;
}
</style>
